import React from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { ParticleField } from "../components/effects/ParticleField";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Icon } from "../components/Icon";
import { IkkeAleneForm } from "../components/IkkeAleneForm";
import { IKKE_ALENE } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

export default function IkkeAlene() {
  const { t } = useLang();
  const d = IKKE_ALENE;

  return (
    <>
      {/* 1. Hero */}
      <section data-testid="ikke-alene-hero" className="relative overflow-hidden pt-40 pb-24 md:pt-52 md:pb-32 border-b border-white/5">
        <div className="absolute inset-0 opacity-50">
          <ParticleField density={34} />
        </div>
        <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[30vw] w-[64vw] rounded-full bg-[#F59E0B]/14 blur-[140px] animate-breathe pointer-events-none" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-12 text-center">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-mono text-xs tracking-[0.28em] uppercase text-[#fcd9a0] inline-flex items-center gap-3"
          >
            <span className="inline-block h-px w-8 bg-[#F59E0B]/50" />
            {t(d.hero.eyebrow)}
            <span className="inline-block h-px w-8 bg-[#F59E0B]/50" />
          </motion.span>
          <MaskedLines
            className="mt-6"
            delay={0.15}
            lineClassName="text-4xl sm:text-6xl md:text-7xl font-medium tracking-tighter text-white leading-[1.05] text-balance"
            lines={[t(d.hero.title)]}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-7 mx-auto max-w-2xl text-lg md:text-xl text-white/55 leading-relaxed"
          >
            {t(d.hero.intro)}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <a
              href="#collaborate"
              data-testid="ikke-alene-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-8 py-4 text-sm font-medium text-[#1a1204] transition-all duration-300 shadow-[0_0_30px_rgba(245,158,11,0.35)] hover:bg-[#fbbf4d] hover:shadow-[0_0_45px_rgba(245,158,11,0.55)]"
            >
              {t(d.hero.ctaPrimary)}
            </a>
            <a
              href="#principles"
              data-testid="ikke-alene-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-sm font-medium text-white/70 hover:text-white hover:border-white/30 transition-all duration-300"
            >
              {t(d.hero.ctaSecondary)}
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. Why this vision exists */}
      <section data-testid="ikke-alene-why" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
              {t(d.why.heading)}
            </h2>
          </Reveal>
          {d.why.paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <p className="mt-6 text-lg text-white/55 leading-relaxed">{t(p)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 3. The vision */}
      <section data-testid="ikke-alene-vision" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <h2 className="max-w-2xl text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
              {t(d.vision.heading)}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {d.vision.cards.map((card, i) => (
              <Reveal key={i} delay={(i % 4) * 0.08}>
                <div data-testid={`ikke-alene-vision-card-${i}`} className="h-full rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#F59E0B]/25 bg-[#F59E0B]/10 text-[#fcd9a0]">
                    <Icon name={card.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-medium tracking-tight text-white">{t(card.title)}</h3>
                  <p className="mt-3 text-sm text-white/50 leading-relaxed">{t(card.text)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 text-sm text-white/35 italic">{t(d.vision.note)}</p>
          </Reveal>
        </div>
      </section>

      {/* 4. Guiding principles */}
      <section id="principles" data-testid="ikke-alene-principles" className="relative py-20 md:py-28 border-b border-white/5 scroll-mt-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
              {t(d.principles.heading)}
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-5 max-w-2xl text-lg text-white/55 leading-relaxed">{t(d.principles.intro)}</p>
          </Reveal>
          <div className="mt-12 grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {d.principles.items.map((item, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <div data-testid={`ikke-alene-principle-${i}`} className="flex items-center gap-3 border-t border-white/10 pt-4">
                  <span className="font-mono text-xs text-[#fcd9a0]/70">0{i + 1}</span>
                  <span className="text-white/75">{t(item)}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. What Ikke Alene is not */}
      <section data-testid="ikke-alene-boundaries" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
              {t(d.boundaries.heading)}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-2xl border border-[#F59E0B]/25 border-l-4 bg-white/[0.02] p-6 md:p-8">
              <div className="flex items-start gap-3">
                <Info className="mt-1 h-5 w-5 shrink-0 text-[#F59E0B]" />
                <div className="space-y-4">
                  {d.boundaries.paragraphs.map((p, i) => (
                    <p key={i} className="text-white/65 leading-relaxed">{t(p)}</p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Current status */}
      <section data-testid="ikke-alene-status" className="relative py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
              {t(d.status.heading)}
            </h2>
          </Reveal>
          {d.status.paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <p className="mt-5 max-w-2xl text-lg text-white/55 leading-relaxed">{t(p)}</p>
            </Reveal>
          ))}
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {d.status.roadmap.map((step, i) => (
              <Reveal key={i} delay={(i % 5) * 0.06}>
                <div data-testid={`ikke-alene-roadmap-${i}`} className="border-t border-white/10 pt-5">
                  <span className="font-mono text-xs text-white/30">0{i + 1}</span>
                  <p className="mt-3 text-white/75 leading-snug">{t(step)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Collaboration */}
      <section id="collaborate" data-testid="ikke-alene-collaborate" className="relative py-20 md:py-28 border-b border-white/5 scroll-mt-24">
        <div className="mx-auto max-w-6xl px-6 md:px-12 grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <Reveal>
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tighter text-white text-balance leading-[1.1]">
                {t(d.collaborate.heading)}
              </h2>
              <p className="mt-6 text-white/55 leading-relaxed">{t(d.collaborate.text)}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <IkkeAleneForm />
          </Reveal>
        </div>
      </section>

      {/* 8. Footer disclaimer */}
      <section data-testid="ikke-alene-disclaimer" className="relative py-12">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <p className="text-center text-sm text-white/35 leading-relaxed">{t(d.footerDisclaimer)}</p>
        </div>
      </section>
    </>
  );
}
